import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, FileDown, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { usePdfExport } from '@/hooks/usePdfExport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorWIP() {
  const [wip, setWip] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vendorFilter, setVendorFilter] = useState('all');
  const { exporting, handleExport } = usePdfExport('/export/vendor-wip-pdf', 'Vendor_WIP');

  const fetchWIP = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/stock/vendor-wip`, {});
      setWip(response.data);
    } catch (err) { console.error(err); toast.error('Failed to fetch vendor WIP'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchWIP(); }, [fetchWIP]);

  const vendors = useMemo(() => {
    const map = {};
    wip.forEach(w => { if (w.vendor_id && !map[w.vendor_id]) map[w.vendor_id] = w.vendor_name || w.vendor_id; });
    return Object.entries(map).sort((a, b) => a[1].localeCompare(b[1]));
  }, [wip]);

  const filtered = useMemo(() => {
    if (vendorFilter === 'all') return wip;
    return wip.filter(w => w.vendor_id === vendorFilter);
  }, [wip, vendorFilter]);

  const totalQty = filtered.reduce((s, w) => s + w.qty, 0);
  const totalIssued = filtered.reduce((s, w) => s + (w.issued || 0), 0);
  const totalReceived = filtered.reduce((s, w) => s + (w.received || 0), 0);

  return (
    <div className="space-y-6" data-testid="vendor-wip-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="vendor-wip-title">VENDOR WIP</h1>
          <p className="text-sm text-text-muted mt-2">Work in progress with external vendors</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-pdf-button" onClick={() => handleExport()} disabled={exporting || wip.length === 0} className="bg-green-600 text-white hover:bg-green-700 rounded-sm font-medium">
            <FileDown className="w-4 h-4 mr-2" />{exporting ? 'EXPORTING...' : 'EXPORT PDF'}
          </Button>
          <Button data-testid="refresh-wip-button" onClick={fetchWIP} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-medium">
            <RefreshCw className="w-4 h-4 mr-2" />REFRESH
          </Button>
        </div>
      </div>

      {vendors.length > 0 && (
        <div className="bg-white border border-border rounded-sm p-4">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-text-muted" />
            <label className="text-xs font-medium uppercase tracking-wider text-text-muted">Vendor</label>
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger data-testid="vendor-filter" className="w-64 rounded-sm">
                <SelectValue placeholder="All Vendors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vendors</SelectItem>
                {vendors.map(([id, name]) => (
                  <SelectItem key={id} value={id}>{name} ({id})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {vendorFilter !== 'all' && (
              <span className="text-xs text-text-muted font-mono">
                {filtered.length} items | Issued: {totalIssued.toLocaleString()} | Received: {totalReceived.toLocaleString()} | Pending: {totalQty.toLocaleString()} pcs
              </span>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-text-muted">Loading WIP data...</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-text-muted">
            {vendorFilter !== 'all' ? 'No WIP data for selected vendor' : 'No vendor WIP data available'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm" data-testid="vendor-wip-table">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Part Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Stage</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Issued</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Received</th>
                  <th className="h-10 px-4 text-right align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Pending</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => (
                  <tr key={idx} className="border-b transition-colors hover:bg-muted/50" data-testid={`vendor-wip-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{item.vendor_id}</td>
                    <td className="p-4 align-middle text-xs">{item.vendor_name || '-'}</td>
                    <td className="p-4 align-middle font-mono text-xs">{item.part_id}</td>
                    <td className="p-4 align-middle text-xs">{item.part_name || '-'}</td>
                    <td className="p-4 align-middle">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-amber-50 text-amber-700">{item.stage}</span>
                    </td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{(item.issued || 0).toLocaleString()}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs">{(item.received || 0).toLocaleString()}</td>
                    <td className="p-4 align-middle text-right font-mono text-xs font-semibold">
                      <span className={item.qty < 0 ? 'text-red-600' : item.qty > 0 ? 'text-amber-700' : 'text-emerald-600'}>
                        {item.qty.toLocaleString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
